/**
 * Seeded PRNG — the ONE allowed source of randomness in Fairway Clash
 * (HANDOFF §2 / §4.3). Every random draw in the sim (slope, scatter, AI jitter,
 * hazard placement, wind) must come from a function created here, so the whole
 * simulation is deterministic given a seed: reproducible tests, shareable seeds,
 * replays, and a path to server-authoritative multiplayer.
 *
 * This is the ONLY file where `Math.random` is permitted by the ESLint
 * determinism guard — and even here we don't use it; mulberry32 is fully seeded.
 *
 * This module DOES contain real logic (per Phase 0 brief); every other sim
 * module is a typed stub for Phase 1.
 */
import type { Rng } from './types.ts';

/**
 * Create a seeded pseudo-random generator (mulberry32).
 * Returns a function producing floats in [0, 1).
 *
 * @param seed any 32-bit-ish integer seed
 */
export function createRng(seed: number): Rng {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Convenience: a seed derived from wall-clock time, for non-deterministic
 * sessions (e.g. a fresh casual match). Tests should pass a fixed seed instead.
 *
 * Note: uses Date.now(), NOT Math.random — determinism is preserved once a seed
 * is captured.
 */
export function timeSeed(): number {
  return Date.now() >>> 0;
}
